/**
 * Text Stroke Utilities
 *
 * Shared utilities for creating consistent text stroke effects across the application.
 * Text stroke creates outlined text for better readability on various backgrounds.
 */

/**
 * Creates a fixed-width text stroke style
 *
 * @param width - The stroke width (e.g., '1px', '2.5px')
 * @returns Style object with WebkitTextStroke and paintOrder
 *
 * @example
 * const style = createTextStroke('1px');
 * // Returns: { WebkitTextStroke: '1px #000', paintOrder: 'stroke fill' }
 */
export const createTextStroke = (width: string) => ({
  WebkitTextStroke: `${width} #000`,
  paintOrder: 'stroke fill' as const
});

/**
 * Creates a responsive text stroke style that scales with font size
 *
 * @param minPx - Minimum stroke width in pixels
 * @param em - Stroke width in em units (relative to font size)
 * @returns Style object with responsive WebkitTextStroke and paintOrder
 *
 * @example
 * const style = createResponsiveTextStroke(1, 0.06);
 * // Returns: { WebkitTextStroke: 'max(1px, 0.06em) #000', paintOrder: 'stroke fill' }
 */
export const createResponsiveTextStroke = (minPx: number, em: number) => ({
  WebkitTextStroke: `max(${String(minPx)}px, ${String(em)}em) #000`,
  paintOrder: 'stroke fill' as const
});
